import React, { useState, useEffect } from "react";
import { Treemap, Tooltip, ResponsiveContainer } from "recharts";
import LoadingSpinner from "../../../pages/dashboard/LoadingSpinner";



const DENSITY_THRESHOLDS = {
  architecture: { low: 0, medium: 2, high: 5, veryHigh: 10 },
  design: { low: 0, medium: 4, high: 10, veryHigh: 20 },
  implementation: { low: 0, medium: 10, high: 25, veryHigh: 50 },
  testability: { low: 0, medium: 4, high: 10, veryHigh: 20 },
  test: { low: 0, medium: 10, high: 25, veryHigh: 50 }
};

const getColorBySmellDensity = (density, variant = "architecture") => {
  const d = Number(density ?? 0);
  const t = DENSITY_THRESHOLDS[variant] || DENSITY_THRESHOLDS.architecture;

  // same colors, different cutoffs
  if (d >= t.veryHigh) return ["#F04E4D", "#F6817E"]; // very high (red)
  if (d >= t.high)     return ["#F6A42B", "#F7B860"]; // high (orange)
  if (d >= t.medium)   return ["#F7C904", "#fcd31d"]; // medium (yellow-ish bucket per your note)
  if (d > t.low)       return ["#25B9D5", "#64C8DF"]; // low (blue)
  return ["#75C4BF", "#B3DEDB"];                      // none
};

// Tooltip (show name, LOC, smellCount, smellDensity)
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const node = payload[0]?.payload || {};
  const loc = Number(node.loc) || 0;
  const smellCount = Number(node.smellCount) || 0;
  const smellDensity = Number(node.smellDensity) || 0;

  return (
    <div
      style={{
        background: "white",
        padding: 10,
        border: "1px solid #ddd",
        borderRadius: 6,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        maxWidth: 320,
      }}
    >
      <strong style={{ display: "block", marginBottom: 6 }}>{node.name}</strong>

      <div style={{ fontSize: 13, lineHeight: 1.4 }}>
        <div><b>LOC:</b> {loc.toLocaleString()}</div>
        <div><b>Smell Count:</b> {smellCount.toLocaleString()}</div>
        <div><b>Smell Density:</b> {smellDensity.toFixed(2)}</div>
      </div>
    </div>
  );
};

// Custom Node renderer (color by smellDensity)
const CustomNode = (props) => {
  const {
    x, y, width, height,
    name, index,
    loc,
    smellDensity,
    variant, // <-- injected from TreeMapChart
  } = props;

  const density = Number(smellDensity) || 0;

  // make gradient id safe + stable-ish
  const gradientId = `grad-${variant}-${name?.replace(/\W/g,'')}-${index ?? 0}`;
  const [startColor, endColor] = getColorBySmellDensity(density, variant);

  const showLabel = width > 60 && height > 28;

  return (
    <g>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={startColor} stopOpacity={1} />
          <stop offset="100%" stopColor={endColor} stopOpacity={1} />
        </linearGradient>
      </defs>

      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        stroke="#fff"
        fill={`url(#${gradientId})`}
        rx={4}
        ry={4}
      />

      {showLabel && name && (
        <text
          x={x + 6}
          y={y + 16}
          fill="#fff"
          fontSize={12}
          fontWeight="600"
          pointerEvents="none"
        >
          {name.length > 28 ? name.slice(0, 27) + "…" : name}
        </text>
      )}
    </g>
  );
};

const TreeMapChart = ({ data, title, variant = "architecture" }) => {
  const [isLoading, setIsLoading] = useState(false);

  const filteredData = (data || []).filter(d => Number(d.loc) > 0);

  useEffect(() => {
    if (data && data.length > 0) {
      setIsLoading(true);
      const timeout = setTimeout(() => setIsLoading(false), 300);
      return () => clearTimeout(timeout);
    }
    setIsLoading(false);
  }, [data]);

  return (
    <div className="card shadow-sm p-3">
      <h5 className="mb-3">{title}</h5>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
          <LoadingSpinner title="Loading smell density..." />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={600}>
          <Treemap
            data={filteredData}
            dataKey="loc"
            isAnimationActive={false}
            aspectRatio={1.3}
            // Inject variant into CustomNode via props
            content={(nodeProps) => <CustomNode {...nodeProps} variant={variant} />}
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TreeMapChart;
